import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockRepairShops } from '../../mockData';
import RatingStars from '../common/RatingStars';
import './Pages.css';

function RepairPage() {
  const navigate = useNavigate();
  // State for the repair shop finder
  const [deviceType, setDeviceType] = useState('');
  const [userLocation, setUserLocation] = useState('');
  const [nearestShops, setNearestShops] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [locations, setLocations] = useState([]);

  // Mock device types for repair
  const deviceTypes = [
    { id: 'smartphone', name: 'Smartphone', icon: '📱' },
    { id: 'laptop', name: 'Laptop/Computer', icon: '💻' },
    { id: 'camera', name: 'Camera', icon: '📷' },
    { id: 'gaming', name: 'Gaming Console', icon: '🎮' },
    { id: 'audio', name: 'Audio Equipment', icon: '🎧' },
    { id: 'apple', name: 'Apple Device', icon: '🍎' }
  ];

  // Mock HK locations
  useEffect(() => {
    // Simulating loading locations from an API
    setTimeout(() => {
      setLocations([
        { id: 'central', name: 'Central', district: 'Hong Kong Island' },
        { id: 'wanchai', name: 'Wan Chai', district: 'Hong Kong Island' },
        { id: 'causeway', name: 'Causeway Bay', district: 'Hong Kong Island' },
        { id: 'mongkok', name: 'Mong Kok', district: 'Kowloon' },
        { id: 'tsimsha', name: 'Tsim Sha Tsui', district: 'Kowloon' },
        { id: 'kwun', name: 'Kwun Tong', district: 'Kowloon' },
        { id: 'tsuen', name: 'Tsuen Wan', district: 'New Territories' },
        { id: 'shatin', name: 'Sha Tin', district: 'New Territories' }
      ]);
    }, 500);
  }, []);

  // Map device types to specializations
  const deviceToSpecialization = {
    'smartphone': ['Smartphones & Tablets', 'Apple Products'],
    'laptop': ['Laptops & Computers', 'Apple Products'],
    'camera': ['Cameras & Photography Equipment'],
    'gaming': ['Gaming Consoles & Accessories'],
    'audio': ['Audio Equipment & Headphones'],
    'apple': ['Apple Products']
  };

  // Add mock distances to shops based on user location
  const getShopsWithDistance = (location) => {
    // This would normally be calculated with actual coordinates
    // For demo purposes, we'll use a simple mapping
    const distanceMap = {
      'central': { 'repair002': 0.2, 'repair004': 1.5, 'repair001': 3.8, 'repair003': 4.2, 'repair005': 5.1, 'repair006': 9.5 },
      'wanchai': { 'repair004': 0.3, 'repair002': 1.2, 'repair001': 3.5, 'repair003': 4.0, 'repair005': 4.8, 'repair006': 8.7 },
      'causeway': { 'repair004': 1.1, 'repair002': 1.8, 'repair005': 3.2, 'repair001': 3.9, 'repair003': 4.5, 'repair006': 9.1 },
      'mongkok': { 'repair001': 0.5, 'repair003': 0.9, 'repair005': 2.8, 'repair002': 3.7, 'repair004': 4.3, 'repair006': 7.9 },
      'tsimsha': { 'repair001': 1.2, 'repair003': 1.8, 'repair002': 2.5, 'repair004': 3.1, 'repair005': 3.8, 'repair006': 8.4 },
      'kwun': { 'repair005': 1.1, 'repair001': 3.4, 'repair003': 3.9, 'repair002': 5.1, 'repair004': 5.6, 'repair006': 6.3 },
      'tsuen': { 'repair006': 0.8, 'repair001': 6.7, 'repair003': 7.2, 'repair005': 7.8, 'repair002': 8.9, 'repair004': 9.5 },
      'shatin': { 'repair006': 3.5, 'repair005': 5.2, 'repair001': 6.9, 'repair003': 7.4, 'repair002': 8.1, 'repair004': 8.6 }
    };

    return mockRepairShops.map(shop => ({
      ...shop,
      distance: distanceMap[location][shop.id] || Math.random() * 10
    }));
  };

  // Handle form submission
  const handleFindRepairShop = (e) => {
    e.preventDefault();
    if (!deviceType || !userLocation) {
      alert('Please select both a device type and your location');
      return;
    }

    // Get shops with distance based on location
    const shopsWithDistance = getShopsWithDistance(userLocation);
    
    // Filter shops by device type
    const matchingShops = shopsWithDistance.filter(shop => {
      return deviceToSpecialization[deviceType].includes(shop.specialization);
    });

    // Sort by distance
    const sortedShops = matchingShops.sort((a, b) => a.distance - b.distance);
    
    setNearestShops(sortedShops);
    setHasSearched(true);
  };

  // Handle subscription button click
  const handleSubscription = (plan, price) => {
    navigate('/payment', {
      state: {
        paymentType: 'shopListing',
        amount: price,
        title: `${plan} Shop Listing (Annual)`,
        returnPath: '/repair-shops'
      }
    });
  };

  return (
    <div className="page repair-page">
      <div className="repair-header">
        <h1>Find Local Repair Experts</h1>
        <p>Trusted repair shops to fix your tech devices in Hong Kong.</p>
      </div>
      
      {/* Repair Shop Finder Section */}
      <div className="repair-finder-section">
        <h2 className="finder-title">Find Nearest Repair Shop</h2>
        <p className="finder-subtitle">Tell us what you need to repair and your location to find the closest expert</p>
        
        <div className="repair-finder-container">
          <form className="repair-finder-form" onSubmit={handleFindRepairShop}>
            <div className="device-type-selector">
              <h3>What do you need to repair?</h3>
              <div className="device-options">
                {deviceTypes.map(device => (
                  <div 
                    key={device.id}
                    className={`device-option ${deviceType === device.id ? 'active' : ''}`}
                    onClick={() => setDeviceType(device.id)}
                  >
                    <div className="device-icon">{device.icon}</div>
                    <div className="device-name">{device.name}</div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="location-selector">
              <h3>Where are you located?</h3>
              <select 
                value={userLocation}
                onChange={(e) => setUserLocation(e.target.value)}
                className="location-dropdown"
              >
                <option value="">Select your location</option>
                {locations.map(location => (
                  <optgroup key={location.district} label={location.district}>
                    {locations
                      .filter(loc => loc.district === location.district)
                      .map(loc => (
                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                      ))
                    }
                  </optgroup>
                ))}
              </select>
            </div>
            
            <button type="submit" className="find-shop-button">
              Find Nearest Repair Shop
            </button>
          </form>
          
          {hasSearched && (
            <div className="search-results">
              <h3 className="results-title">
                {nearestShops.length > 0 
                  ? `Found ${nearestShops.length} repair shops for your ${deviceTypes.find(d => d.id === deviceType).name}`
                  : `No repair shops found for ${deviceTypes.find(d => d.id === deviceType).name} in your area`
                }
              </h3>
              
              {nearestShops.length > 0 && (
                <div className="nearest-shop-highlight">
                  <div className="highlight-label">Nearest Shop</div>
                  <div className="highlight-content">
                    <h4>{nearestShops[0].name}</h4>
                    <RatingStars 
                      rating={nearestShops[0].rating} 
                      className="highlight-rating"
                    />
                    <p className="highlight-distance">{nearestShops[0].distance.toFixed(1)} km away</p>
                    <p className="highlight-address">{nearestShops[0].address}</p>
                    <div className="highlight-actions">
                      <a href={`tel:${nearestShops[0].contactNumber.replace(/\s/g, '')}`} className="call-button">
                        Call Now
                      </a>
                      <a href={`https://maps.google.com/?q=${encodeURIComponent(nearestShops[0].address)}`} target="_blank" rel="noopener noreferrer" className="directions-button">
                        Get Directions
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      <h2 className="all-shops-title">All Repair Shops</h2>
      
      <div className="repair-shops-list">
        {mockRepairShops.map(shop => (
          <div key={shop.id} className="repair-shop-item">
            <div className="repair-shop-content">
              <div className="repair-shop-info">
                <h2 className="repair-shop-name">{shop.name}</h2>
                <div className="repair-shop-rating">
                  <RatingStars 
                    rating={shop.rating} 
                    showCount={true} 
                    count={shop.reviewCount} 
                    source="Google reviews"
                  />
                </div>
                <div className="repair-shop-specialization">
                  <span className="info-label">Specialization:</span> {shop.specialization}
                </div>
                <div className="repair-shop-address">
                  <span className="info-label">Address:</span> {shop.address}
                </div>
                <div className="repair-shop-contact">
                  <span className="info-label">Contact:</span> {shop.contactNumber}
                </div>
                <div className="repair-shop-hours">
                  <span className="info-label">Hours:</span> {shop.openingHours}
                </div>
                <p className="repair-shop-blurb">{shop.shortBlurb}</p>
                <div className="shop-actions">
                  <a href={`tel:${shop.contactNumber.replace(/\s/g, '')}`} className="contact-shop-button">
                    Call Shop
                  </a>
                  <a href={`https://maps.google.com/?q=${encodeURIComponent(shop.address)}`} target="_blank" rel="noopener noreferrer" className="view-map-button">
                    View on Map
                  </a>
                </div>
              </div>
              
              <div className="repair-shop-image">
                <img src={shop.promoImage} alt={shop.name} />
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Promote your repair shop section */}
      <div className="list-your-shop-section">
        <h2 className="section-title">List Your Repair Shop</h2>
        <p className="section-subtitle">Get discovered by thousands of potential customers looking for repair services in Hong Kong</p>
        
        <div className="pricing-tiers single-tier">
          <div className="pricing-card featured">
            <div className="featured-badge">Featured Listing</div>
            <h3 className="tier-name">Repair Shop Listing</h3>
            <div className="price">
              <span className="currency">HK$</span>
              <span className="amount">4,000</span>
              <span className="period">/year</span>
            </div>
            <ul className="features-list">
              <li>Business name & address</li>
              <li>Contact phone number & website link</li>
              <li>Company logo & promotional photos</li>
              <li>Detailed service descriptions</li>
            </ul>
            <button 
              className="subscribe-button featured-button"
              onClick={() => handleSubscription('Standard', 4000)}
            >
              List Your Shop
            </button>
          </div>
        </div>
        
        <div className="pricing-note">
          <p>Join the trusted network of repair experts on SwapWise HK. After payment, our team will verify your shop's credentials and reputation to ensure it meets our quality standards.</p>
          <p>If your shop doesn't qualify for our platform, you'll receive a full refund of your listing fee. We maintain strict standards to ensure customers can trust all repair shops featured on our platform.</p>
          <p>For assistance or custom packages, contact our sales team at <a href="mailto:business@swapwisehk.com">business@swapwisehk.com</a></p>
        </div>
      </div>
    </div>
  );
}

export default RepairPage; 